import get from 'lodash/get';
import { format } from 'date-fns';
import { createSelector } from 'reselect';
import { camelizeKeys } from 'humps';

import { bugsnagError } from 'services/bugsnag';
import { isValidDate } from 'utils/utils';

import { LAYOUT_ID_OFFSET } from 'utils/constants';

/**
 * Class for managing design status definitions and mappings.
 */
export class Status {
  static IN_PROGRESS = 0;
  static ABANDONED = 1;
  static ACTIVE = 2;
  static PENDING = 4;
  static READY = 5;
  static REJECTED = 6;
  static UNKNOWN = -1;
}

/**
 * Class for managing design source definitions and mappings.
 */
export class Source {
  static INITIAL_DESIGN = 1
  static REDESIGN = 2
  static LIVE_SWAP = 3
  static ELSIE_NEW = 4
  static ELSIE_CLONE = 5
  static BESSIE_REDESIGN = 6
  static SHOWROOM = 7
}

// Author name for all modsy-created designs
const MODSY_DESIGN_ATUTHOR = 'Modsy Team';

/**
 * These are the design properties we care about.
 */
const defaultProps = {
  id: null,
  canLiveSwap: true,
  createdAt: null,
  createdBy: null,
  designIndex: null,
  designType: '',
  layoutInfo: undefined,
  // possible members of layoutInfo
  // recommended,
  // selected,
  // selectedBy,
  // selectedAt,
  // sentAt
  name: '',
  numberOfOwnerViews: 0,
  playground: 0, // 0 = modsy design, >0 = playground design, value indicates base modsy design
  productIds: [],
  renders: [],
  revisions: [], // there can be multiple revisions, store revisions in sequence/time order
  revision: null, /* if non-null, the current/active revision for this design.
                    if null, implies that base design is the current/active revision. */
  parentSpaceId: null,
  source: null,
  statusCode: Status.UNKNOWN,
  updatedAt: null,
  upgraded: false,
  userFeedback: null
};

/**
 * We should consolidate as much of the business logic
 * around Designs as we can here. If you find yourself
 * using multiple design attributes to perform or gate
 * an action, then that logic should probably be delegated
 * as a helper method in this module.
 */
class Design {
  static Source = Source;
  static Status = Status;

  /** Converts warehouse response to our internal mode.
   * We can get full Design model here or a design brief.
   */
  static createDesignFromWarehouseResponse ({ spaceId, whDesign, showroomSpace }) {
    // Reorder renders
    let renders = (whDesign.renders && whDesign.renders.sort((a, b) => {
      if (a.view_360 === b.view_360) {
        return a.layout_order - b.layout_order;
      }

      return a.view_360 ? 1 : -1;
    })) || [];

    // For showroom designs, make sure top down render is last.
    if (whDesign.source === Source.SHOWROOM) {
      renders = renders.sort((a, b) => {
        if (a.view_topdown === b.view_topdown) {
          return 0;
        }

        return a.view_topdown ? 1 : -1;
      });
    }

    // Put the parent design id on renders.
    // Put a description on each render.
    // For 360 renders, the pre-launch image is the first render.
    const angleCnt = renders.reduce((sum, render) => sum + (render.view_360 ? 0 : 1), 0);
    renders = renders.map((render, index) => {
      render.parentDesignId = whDesign.id;
      render.description = `Angle ${index + 1} of ${angleCnt}`;
      if (render.view_360) {
        render.panoimage = render.image;
        render.panoimagefile = render.imagefile;
        render.image = renders[0].image;
        render.imagefile = renders[0].imagefile;
        render.panoramaViewed = false;
        render.description = `360 View`;
      }

      // generate hotspot instance IDs so we can display the selected one properly
      if (render.hotspots && render.hotspots.length) {
        let idxOffset = 0;

        for (let idx = 0; idx < render.hotspots.length; ++idx) {
          if (render.hotspots[idx].instanceId) {
            render.hotspots[idx].instance_id = render.hotspots[idx].instanceId;
          }

          if (render.hotspots[idx].instance_id === undefined) {
            render.hotspots[idx].instance_id = idx + '.' + idxOffset;
            idxOffset++;
          }
        }
      }

      return render;
    });

    // if the first render has any hotspots
    // the design and the associated studio will
    // be considered upgraded.
    // Assumption is all designs and renders in a space
    // will either have hotspots or none at all
    const upgraded = renders.length > 0 &&
      renders[0].hotspots &&
      renders[0].hotspots.length > 0;

    // Create product ids array
    // and product like status object
    // to store on design model
    let productIds = [];
    const products = whDesign.products || [];

    products.forEach(product => {
      const id = product.product_id;
      productIds.push(id);
    });

    // Handle design revisions.
    // We always default to the latest revision on design load.
    const fullRevisions = get(whDesign, 'designsnapshot_history', []);
    const revisions = fullRevisions.map(revision => revision.id);
    const revisionCnt = revisions.length;
    const revision = revisionCnt > 0
      ? revisions[revisionCnt - 1]
      : null;

    // Handle can live swap flag.
    // Here we only want to disable if the flag is passed in as specifically false.
    const canLiveSwap = !!(
      (whDesign.can_live_swap === undefined && whDesign.canLiveSwap === undefined) ||
      whDesign.can_live_swap ||
      whDesign.canLiveSwap
    );

    // layout_info format should look like this:
    // layout_info: {
    //   selected: true | false,
    //   selected_by: <user_id> | null,
    //   selected_at: <datetime> | null,
    //   sent_at: <datetime> | null,
    //   is_recommended: true | false,
    // }
    const layoutInfo = camelizeKeys(whDesign.layout_info);

    return new Design({
      id: whDesign.id,
      canLiveSwap,
      createdAt: whDesign.created_at,
      createdBy: whDesign.created_by,
      designIndex: whDesign.design_id,
      designType: whDesign.design_type,
      layoutInfo,
      name: layoutInfo ? `Layout ${whDesign.design_id - (LAYOUT_ID_OFFSET - 1)}` : `Design ${whDesign.design_id}`,
      numberOfOwnerViews: whDesign.num_views_owner || whDesign.numViewsOwner,
      productIds,
      renders,
      parentSpaceId: spaceId,
      parentSpaceIsShowroom: !!showroomSpace,
      source: whDesign.source, // used to determine if user or modsy created design
      statusCode: whDesign.status,
      updatedAt: whDesign.updated_at || whDesign.updatedAt,
      upgraded,
      userFeedback: whDesign.design_feedback || null,

      // Design Revision/Snapshot/Playground.
      playground: whDesign.playground,
      revisions,
      revision
    });
  }

  /** Creates a new design instance with defaults and optional overrides. */
  constructor (overrides) {
    this.fields = { ...defaultProps, ...overrides };
  }

  /** Creates a new design copied from this with optional overrides. */
  makeCopy (overrides) {
    return new Design({ ...this.fields, ...overrides });
  }

  makeCopyWithRevision ({ revisionUuid, addlProducts, removeProducts, replaceProducts }) {
    let newDesign = this.makeCopy({});
    if (revisionUuid) {
      // Copy all the revisions up to the current revision.
      // Then append the new revision to the end.
      // This splices out the older revisions as expected.
      // Finally, update current revision pointer.
      const currRevisionUuid = newDesign.fields.revision;
      const currRevisions = newDesign.fields.revisions;

      // Slice up to and including currRevisionUuid
      const currRevisionUuidIndex = currRevisions.findIndex((r) => r === currRevisionUuid);
      const revisionsToKeep = currRevisions.slice(0, currRevisionUuidIndex + 1);

      // Add new revision Uuid
      newDesign.fields.revisions = [...revisionsToKeep, revisionUuid];
      newDesign.fields.revision = revisionUuid;
    }

    if (addlProducts) {
      let productIds = [...newDesign.fields.productIds, ...addlProducts];
      newDesign.fields.productIds = productIds;
    }

    if (removeProducts) {
      let productIds = newDesign.fields.productIds.filter(id => !removeProducts.includes(id));
      newDesign.fields.productIds = productIds;
    }

    if (replaceProducts) {
      newDesign.fields.productIds = replaceProducts;
    }

    return newDesign;
  }

  makeCopyUndoRevision () {
    const revisions = this.fields.revisions;
    const currentRevisionUuid = this.revisionUuid();
    const index = revisions.findIndex(revisionUuid => {
      return revisionUuid === currentRevisionUuid;
    });
    if (index < 0) {
      bugsnagError(
        'Undo revision failed',
        { currentRevisionUuid, index, revisions }
      );
      return this;
    }

    const newRevisionIndex = index - 1;
    const undoRevisionUuid = (newRevisionIndex >= 0)
      ? revisions[newRevisionIndex]
      : null;

    return this.makeCopy({ revision: undoRevisionUuid });
  }

  makeCopyRedoRevision () {
    const revisions = this.fields.revisions;
    const currentRevisionUuid = this.revisionUuid();
    const index = revisions.findIndex(revisionUuid => {
      return revisionUuid === currentRevisionUuid;
    });
    if (
      (currentRevisionUuid && index < 0) ||
      (index + 1) >= revisions.length
    ) {
      bugsnagError(
        'Redo revision failed',
        { currentRevisionUuid, index, revisions }
      );
      return this;
    }

    const newRevisionIndex = index + 1;
    const redoRevisionUuid = revisions[newRevisionIndex];
    return this.makeCopy({ revision: redoRevisionUuid });
  }

  makeCopyCancelRevision () {
    let revisions = [...this.fields.revisions];
    const currentRevisionUuid = this.revisionUuid();
    if (
      revisions.length === 0 ||
      revisions[revisions.length - 1] !== currentRevisionUuid
    ) {
      bugsnagError(
        'Canceled revision must be lastest in revision history',
        { currentRevisionUuid, revisions }
      );
      return this;
    }

    revisions.pop();

    const newRevisionIndex = revisions.length - 1;
    const newRevisionUuid = (newRevisionIndex >= 0)
      ? revisions[newRevisionIndex]
      : null;

    return this.makeCopy({ revision: newRevisionUuid, revisions });
  }

  // =======
  // GETTERS
  // =======
  /** Returns the id for this design. */
  id () { return this.fields.id; }

  /** Returns the server design index. Useful for interfacing with legacy design APIs. */
  designIndex () { return this.fields.designIndex; }

  /** Returns true if the id of this design is equal to a givenId */
  hasIdEqualTo (givenId) { return this.fields.id === Number(givenId); }

  /** Returns true if the design is pending w.r.t. user. */
  isPending () {
    return [Status.PENDING, Status.READY, Status.REJECTED].includes(this.fields.statusCode);
  }

  /** returns the status code of the design */
  status () {
    return this.fields.statusCode;
  }

  /** Returns true if the design is ready for viewing by user. */
  canBeViewedByUser () {
    return this.fields.statusCode === Status.ACTIVE;
  }

  /** Return true if the design is pending. */
  isInProgress () {
    return this.fields.statusCode === Status.IN_PROGRESS;
  }

  /** Return true if the design is a layout design */
  isLayout () {
    return !!this.fields.layoutInfo;
  }

  /** Return true if the design is a layout design and is the recommended layout */
  isRecommendedLayout () {
    return get(this.fields.layoutInfo, 'recommended', false);
  }

  /** Return true if the design is a layout design and has been selected */
  isSelectedLayout () {
    return get(this.fields.layoutInfo, 'selected', false);
  }

  /** Returns the userId of the user that selected the layout, or null if automatically selected */
  layoutSelectedBy () {
    return get(this.fields.layoutInfo, 'selectedBy', false);
  }

  /** Returns the datetime at which the layout was selected or null if the layout has not been selected */
  layoutSelectedAt () {
    return get(this.fields.layoutInfo, 'selectedAt', false);
  }

  /** Returns the datetime at which the layout was sent to the user */
  layoutSentAt () {
    return get(this.fields.layoutInfo, 'sentAt', false);
  }

  /** Returns the name for this design. */
  name () { return this.fields.name; }

  /** Returns the Studio name for this design. */
  studioName (userFirstName) {
    if (this.isShowroomDesign()) {
      return `Your ${this.name()}`;
    }

    if (this.createdByUser()) {
      return (userFirstName ? `${userFirstName}'s ` : '') + this.name();
    }

    return `Modsy ${this.name()}`;
  }

  /** Returns the parent space ID for this design. */
  parentSpaceId () { return this.fields.parentSpaceId; }

  /** Returns playground value. */
  playground () { return this.fields.playground; }

  /** Returns true if this is a playground design. */
  isPlayground () { return this.playground() > 0; }

  /** Returns the position id of this design. Used for ordering */
  position () { return this.isPlayground() ? this.playground() : this.designIndex(); }

  /** Returns true if the design has been viewed by owner. */
  viewedByOwner () { return this.fields.numberOfOwnerViews > 0; }

  /**
   * Increments the number of owner views by one.
   * Returns a pure copy with the updated owner view count.
   */
  incrementViewedByOwner () {
    return this.makeCopy({
      numberOfOwnerViews: this.fields.numberOfOwnerViews + 1
    });
  }

  /** Returns true if the design has at least one render. */
  hasRenders () { return this.fields.renders.length > 0; }

  /** Returns the renders for this design
   * DesignFeedback is a conceptual and conditional render.
   * Memoized for efficiency.
   */
  renders = createSelector(
    () => (
      !this.createdByUser() &&
      !this.isShowroomDesign() &&
      (this.requestInitialDesignFeedback() || this.requestAdditionalDesignFeedback())
    ),
    () => this.fields.renders,
    () => this.fields.id,
    () => this.fields.designIndex,
    (includeFeedbackRender, renders, id, designIndex) => {
      if (!includeFeedbackRender) {
        return renders;
      }

      let memoRenders = [...renders];
      if (!this.isLayout()) {
        memoRenders.push({
          id: 'feedback-' + (designIndex),
          parentDesignId: id,
          styleboard_id: renders[0] && renders[0].styleboard_id,
          layout_order: renders.length,
          isFeedbackRender: true
        });
      }
      return memoRenders;
    }
  );

  /** filters the non empty thumbnails of this design. */
  thumbnails () {
    return this.renders()
      .map(render => render.thumbnailfile || render.thumbnail)
      .filter(thumbnail => !!thumbnail);
  }

  /** filters the non empty imagefiles of this design. Used in StudioSummaryPage */
  imagefiles () {
    return this.renders()
      .filter(render => render.imagefile)
      .map(render => render.imagefile);
  }

  /** Returns the id of the first render of this design. */
  firstRenderId () {
    return this.hasRenders() ? this.fields.renders[0].id : null;
  }

  /** Returns the first render of this design. */
  firstRender () {
    return this.hasRenders()
      ? this.fields.renders[0]
      : null;
  }

  /** Returns true if user is on the first render of this design. */
  onFirstRender (currentRender) {
    return this.firstRenderId() === currentRender.id;
  }

  /** Returns true if user is on a render with a string id,
   * meaning on the feedback render of this design. */
  onFeedbackRender (currentRender) {
    return !Number.isInteger(currentRender.id);
  }

  /** Returns render for provided render ID. */
  findRenderById (id) {
    const renders = this.renders();
    return renders.find(render => render.id === id);
  }

  /** Returns render for provided camera. */
  findRenderByCamera (camera) {
    if (!camera) { return this.findRenderById(this.firstRenderId()); }

    const renders = this.renders();
    // this will be improved once render model is fully implemented
    const render = renders.find(render => {
      return render.camera_id === camera;
    });

    return render || this.findRenderById(this.firstRenderId());
  }

  /** Returns the last render of this design. */
  lastRender () {
    return this.hasRenders()
      ? this.renders()[this.renders().length - 1]
      : null;
  }

  /** Returns true if user is on the last render of this design. */
  onLastRender (currentRender) {
    return get(this.lastRender(), 'id') === currentRender.id || this.onFeedbackRender(currentRender);
  }

  /** Returns the index of the supplied render. */
  renderIndex (render) {
    if (!render) { return -1; }

    const renders = this.renders();
    const renderIndex = renders.findIndex(r => r.id === render.id);
    return renderIndex;
  }

  /** Returns the conceptual next render given a current render. */
  nextRender (currRender) {
    const renders = this.renders();
    const renderIndex = this.renderIndex(currRender);
    return renders[(renderIndex + 1) % renders.length];
  }

  /** Returns the conceptual previous render given a current render. */
  previousRender (currRender) {
    const renders = this.renders();
    const renderIndex = this.renderIndex(currRender);
    const prevRenderIndex = renderIndex - 1;
    return prevRenderIndex < 0
      ? renders[renders.length - 1]
      : renders[prevRenderIndex];
  }

  /**
   * Returns the hotspot UUID for a given productId and render.
   * If there are multiple instances of the productId in render
   * then the hotspot UUID associated with the first instance is
   * returned.
   */
  findHotspotUuidFor (productId, render) {
    if (!render || !render.hotspots || !render.hotspots.length) {
      return null;
    }

    const hotspot = render.hotspots.find(hotspot => {
      const hotspotProductId = hotspot.product_id || hotspot.productId;
      return hotspotProductId === productId;
    });

    return hotspot ? (hotspot.instance_id || hotspot.instanceId) : null;
  }

  /**
   * Given a productId, finds the optimal render for that product.
   * @returns {Render} render- The render instance of optimal render if found oth. null.
   */
  findOptimalRenderFor (productId) {
    // For now, optimal means the first render we find where this is a
    // hotspot matching the product ID. This will likely change in the
    // future to be smarter, e.g., choose render where the product is
    // most centralized.
    const renders = this.renders();
    for (const render of renders) {
      const hotspotUuid = this.findHotspotUuidFor(productId, render);
      if (hotspotUuid) {
        return render;
      }
    }

    return null;
  }

  /** Set flag; whether a 360 render has been viewed or not.  */
  render360Viewed (id) {
    let renderIndex = this.fields.renders.findIndex(render => (
      render.id === id
    ));

    if (renderIndex !== -1) { this.fields.renders[renderIndex].hasViewed360 = true; }
  }

  /** Returns the product ids for this design. */
  productIds () { return this.fields.productIds; }

  /** returns if the design is upgraded with hotspots or not . */
  upgraded () { return this.fields.upgraded; }

  /** Returns the creation date of this design */
  createdAt () {
    return this.fields.createdAt;
  }

  /** Returns the updated date of this design */
  updatedAt () {
    return this.fields.updatedAt;
  }

  /** Returns the artist who created this design */
  createdBy () {
    if (this.createdByUser()) { return this.fields.createdBy; } else { return MODSY_DESIGN_ATUTHOR; }
  }

  /** Returns formatted updated/created by copy for this design */
  lastUpdatedCopy (filterOutTeam = false) {
    const updatedDate = new Date(this.updatedAt());
    const createdDate = new Date(this.createdAt());

    const wasUpdated = isValidDate(updatedDate);
    const lastUpdatedDate = wasUpdated ? updatedDate : createdDate;

    // If truthy + valid last updated date, return copy
    if (lastUpdatedDate && isValidDate(lastUpdatedDate)) {
      const formattedLastUpdateDate = format(lastUpdatedDate, 'MMM D, YYYY') || '';
      const formattedCreatedBy = this.createdBy() || '';
      const copy = `${wasUpdated ? 'Updated' : 'Created'} ${formattedLastUpdateDate} by ${formattedCreatedBy}`;
      // Remove 'Team' from 'Modsy Team'
      if (filterOutTeam) {
        return this.filterOutTeamFromModsyTeamText(copy);
      } else {
        return copy;
      }
    }
    return '';
  }

  filterOutTeamFromModsyTeamText (text) {
    return text.replace('Modsy Team', 'Modsy');
  }

  /** Returns true if this design has been created by a User. */
  createdByUser () {
    return [
      Source.LIVE_SWAP,
      Source.BESSIE_REDESIGN
    ].includes(this.fields.source);
  }

  /** Returns true if this is a showroom design. */
  isShowroomDesign () {
    return this.fields.source === Source.SHOWROOM || this.fields.parentSpaceIsShowroom;
  }

  isPureShowroomDesign () {
    return this.fields.source === Source.SHOWROOM;
  }

  /** Returns true if user has already submitted feedback for this design. */
  feedbackAlreadySubmitted () {
    return this.userRating() > 0 && !this.requestAdditionalDesignFeedback();
  }

  /** Returns the user feedback ID for this design. */
  userFeedbackId () {
    return get(this.fields, 'userFeedback.id', null);
  }

  /** Returns the user feedback rating for this design or 0 if unrated. */
  userRating () {
    return get(this.fields, 'userFeedback.rating', 0);
  }

  /**
   * Returns true if we should ask for initial user feedback.
   * Handles case where we have NOT yet received information from
   * server on whether or not feedback has already been provided
   * for the design.
   */
  requestInitialDesignFeedback () {
    return this.fields.userFeedback !== null &&
      !this.requestAdditionalDesignFeedback() &&
      this.userRating() === 0 &&
      !this.createdByUser();
  }

  /**
   * Decides whether or not we should ask the user for additional feedback.
   * This is the case when user has provided a rating but has not told us
   * which categories we could improve on.
   */
  requestAdditionalDesignFeedback () {
    if (
      this.userRating() === 0 ||
      get(this.fields, 'userFeedback.address_my_notes', false) ||
      get(this.fields, 'userFeedback.color_choices', false) ||
      get(this.fields, 'userFeedback.furniture_layout', false) ||
      get(this.fields, 'userFeedback.product_selection', false) ||
      get(this.fields, 'userFeedback.style_match', false) ||
      get(this.fields, 'userFeedback.three_d_room_model', false) ||
      this.createdByUser()
    ) {
      return false;
    }

    return true;
  }

  /**
   * Returns whether or not this design supports live swaps.
   */
  canLiveSwap () {
    return this.fields.canLiveSwap;
  }

  /**
   * Returns whether or not this design has revisions.
   */
  hasRevisions () {
    return this.revisionCnt() > 0;
  }

  /**
   * Returns the number of revisions in this design.
   */
  revisionCnt () {
    return this.fields.revisions.length;
  }

  /**
   * Returns the current revision UUID.
   */
  revisionUuid () {
    return this.fields.revision;
  }

  /**
   * Returns whether or not the current revision
   * is also the first revision in the design.
   */
  onFirstRevision () {
    const currRev = this.revisionUuid();
    const revisions = this.fields.revisions;
    const revisionsCnt = revisions.length;

    if (!currRev) {
      return revisionsCnt === 0;
    }

    return currRev === revisions[0];
  }

  /**
   * Returns whether or not the current revision
   * is also the most recent (last) revision in the design.
   */
  onMostRecentRevision () {
    const currRev = this.revisionUuid();
    const revisions = this.fields.revisions;
    const revisionsCnt = revisions.length;

    if (!currRev) {
      return revisionsCnt === 0;
    }

    return currRev === revisions[revisionsCnt - 1];
  }

  /** Returns the revision history starting from the current revision
   * back to the first revision. This orders revisions from most recent
   * to least recent.
   *
   * Memoized for efficiency.
   */
  revisionHistory = createSelector(
    () => this.revisionUuid(),
    () => this.fields.revisions,
    (currRevisionUuid, revisions) => {
      if (!currRevisionUuid) { return []; }

      const index = revisions.findIndex(revisionUuid => revisionUuid === currRevisionUuid);
      if (index < 0) { return []; }

      let history = [];
      for (let i = index; i >= 0; i--) {
        history.push(revisions[i]);
      }

      return history;
    }
  );

  setAuthorInfo (createdAt, createdBy) {
    this.fields.createdAt = createdAt;
    this.fields.createdBy = createdBy;
  }

  getSegmentProperties () {
    return {
      id: this.id(),
      number: this.designIndex(),
      stylist: this.createdBy()
    };
  }
}

export default Design;
